const COOKIE_PREFIX = 'eating_admin_'
const STORAGE_META_KEY = 'eating_admin_session_meta'
const STORAGE_LOGOUT_REASON_KEY = 'eating_admin_logout_reason'
const REMEMBER_EXPIRE_MS = 24 * 60 * 60 * 1000
const INACTIVE_TIMEOUT_MS = 30 * 60 * 1000
let activityTrackingInited = false
let activityTimer = null
let lastTouchMs = 0

function setCookie(name, value, days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${COOKIE_PREFIX}${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`
}

function getCookie(name) {
  const key = `${COOKIE_PREFIX}${name}=`
  const parts = document.cookie ? document.cookie.split('; ') : []
  const found = parts.find(p => p.startsWith(key))
  if (!found) return null
  return decodeURIComponent(found.slice(key.length))
}

function removeCookie(name) {
  document.cookie = `${COOKIE_PREFIX}${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

function readSessionMeta() {
  try {
    const raw = localStorage.getItem(STORAGE_META_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (_e) {
    return null
  }
}

function writeSessionMeta(meta) {
  try {
    localStorage.setItem(STORAGE_META_KEY, JSON.stringify(meta || {}))
  } catch (_e) {
    // ignore storage failure
  }
}

function clearSessionMeta() {
  try {
    localStorage.removeItem(STORAGE_META_KEY)
  } catch (_e) {
    // ignore storage failure
  }
}

function setLogoutReason(reason) {
  const s = String(reason || '').trim()
  if (!s) return
  try {
    sessionStorage.setItem(STORAGE_LOGOUT_REASON_KEY, s)
  } catch (_e) {
    // ignore storage failure
  }
}

function nowMs() {
  return Date.now()
}

function getSessionState() {
  const isLoggedIn = getCookie('isLoggedIn') === 'true'
  const token = getCookie('adminToken')
  if (!isLoggedIn || !token) {
    return { valid: false, reason: 'missing_auth' }
  }

  const meta = readSessionMeta()
  const now = nowMs()
  if (!meta) {
    return { valid: true, reason: '' }
  }

  const rememberMe = !!meta.rememberMe
  const issuedAt = Number(meta.issuedAt || 0)
  const expiresAt = Number(meta.expiresAt || 0)
  const lastActiveAt = Number(meta.lastActiveAt || issuedAt || 0)

  if (rememberMe) {
    if (expiresAt > 0 && now >= expiresAt) {
      return { valid: false, reason: 'expired_24h' }
    }
    return { valid: true, reason: '' }
  }

  if (lastActiveAt > 0 && now - lastActiveAt > INACTIVE_TIMEOUT_MS) {
    return { valid: false, reason: 'inactive_30m' }
  }
  return { valid: true, reason: '' }
}

export function setAuthSession({ userInfo, isLoggedIn, token, rememberMe }) {
  const now = nowMs()
  const keep = !!rememberMe
  const cookieDays = 1
  setCookie('isLoggedIn', isLoggedIn ? 'true' : 'false', cookieDays)
  setCookie('userInfo', JSON.stringify(userInfo || {}), cookieDays)
  if (token != null && String(token).trim() !== '') {
    setCookie('adminToken', String(token).trim(), cookieDays)
  } else {
    removeCookie('adminToken')
  }
  writeSessionMeta({
    rememberMe: keep,
    issuedAt: now,
    expiresAt: keep ? now + REMEMBER_EXPIRE_MS : 0,
    idleTimeoutMs: keep ? 0 : INACTIVE_TIMEOUT_MS,
    lastActiveAt: now
  })
}

export function clearAuthSession(reason = '') {
  removeCookie('isLoggedIn')
  removeCookie('userInfo')
  removeCookie('adminToken')
  clearSessionMeta()
  setLogoutReason(reason)
}

export function getAdminToken() {
  return getCookie('adminToken')
}

/** 供独立 axios 调用（与 $api 一致） */
export function getAdminAuthHeader() {
  const t = getAdminToken()
  return t ? { Authorization: `Bearer ${t}` } : {}
}

export function getIsLoggedIn() {
  const state = getSessionState()
  if (!state.valid) {
    clearAuthSession(state.reason)
    return false
  }
  return true
}

export function getUserInfo() {
  const raw = getCookie('userInfo')
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch (e) {
    return {}
  }
}

/** 合并更新本地会话中的 userInfo（不改 token / 登录态） */
export function patchUserInfo(partial) {
  const prev = getUserInfo() || {}
  const next = Object.assign({}, prev, partial || {})
  const meta = readSessionMeta() || {}
  const keep = !!meta.rememberMe
  setCookie('userInfo', JSON.stringify(next), 1)
  writeSessionMeta({
    rememberMe: keep,
    issuedAt: Number(meta.issuedAt || Date.now()),
    expiresAt: Number(meta.expiresAt || 0),
    idleTimeoutMs: keep ? 0 : INACTIVE_TIMEOUT_MS,
    lastActiveAt: Number(meta.lastActiveAt || Date.now())
  })
  return next
}

/** 员工、收银员：后台禁止删除（与接口 DELETE 403 一致，用于隐藏按钮） */
export function canAdminDelete() {
  const r = String(getUserInfo().roleLabel || '').trim()
  return r !== '员工' && r !== '收银员'
}

export function isSuperAdminUser() {
  return String(getUserInfo().roleLabel || '').trim() === '超级管理员'
}

export function isStaffOrCashierUser() {
  const r = String(getUserInfo().roleLabel || '').trim()
  return r === '员工' || r === '收银员'
}

export function touchSessionActivity() {
  const meta = readSessionMeta()
  if (!meta || meta.rememberMe) return
  const now = nowMs()
  if (now - lastTouchMs < 1000) return
  meta.lastActiveAt = now
  writeSessionMeta(meta)
  lastTouchMs = now
}

export function ensureAuthSessionValid({ touch = false } = {}) {
  const state = getSessionState()
  if (!state.valid) {
    clearAuthSession(state.reason)
    return state
  }
  if (touch) touchSessionActivity()
  return state
}

export function consumeLogoutReason() {
  try {
    const r = sessionStorage.getItem(STORAGE_LOGOUT_REASON_KEY) || ''
    sessionStorage.removeItem(STORAGE_LOGOUT_REASON_KEY)
    return r
  } catch (_e) {
    return ''
  }
}

function redirectToLoginIfNeeded() {
  if (typeof window === 'undefined') return
  if (String(window.location.hash || '').includes('#/login')) return
  window.location.hash = '#/login'
}

export function initAuthActivityTracking() {
  if (activityTrackingInited || typeof window === 'undefined') return
  activityTrackingInited = true

  const onActivity = () => touchSessionActivity()
  ;['click', 'keydown', 'mousemove', 'touchstart', 'scroll'].forEach((evt) => {
    window.addEventListener(evt, onActivity, { passive: true })
  })

  activityTimer = window.setInterval(() => {
    const state = getSessionState()
    if (state.valid) return
    if (state.reason === 'inactive_30m' || state.reason === 'expired_24h') {
      clearAuthSession(state.reason)
      redirectToLoginIfNeeded()
    }
  }, 10000)
}

